import {
  Alert,
  AspectRatio,
  Button,
  Card,
  CardContent,
  CircularProgress,
  colors,
  Divider,
  Link,
  List,
  ListItem,
  ListItemDecorator,
  Skeleton,
  Stack,
  Typography,
} from "@mui/joy";
import { PageBase } from "app/components/Common/PageBase";
import { PageBodyContainer } from "app/components/Common/PageBodyContainer";
import SubjectIcon from "app/components/SubjectIcon/SubjectIcon";
import useCheckoutPage from "./utils/useCheckoutPage";
import Markdown from "app/components/Markdown/Markdown";
import dayjs from "dayjs";
import { DATE_FORMAT, TIME_FORMAT } from "app/constants/content";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const CheckoutPage = () => {
  const {
    exam,
    handleBuy,
    loading,
    hasQuota,
    isHL,
    isSL,
    hlQuota,
    slQuota,
    userInfo,
    bundleInfo,
    quotasRequired,
  } = useCheckoutPage({});

  const date = exam && dayjs(exam?.examTime);

  const SkeletonText = <Skeleton>Lorem ipsum dolor sit amet</Skeleton>;

  return (
    <PageBase>
      <PageBodyContainer>
        <Stack spacing={3} sx={{ p: 1 }}>
          <Stack spacing={3} direction="row" alignItems={"end"}>
            <Stack spacing={3}>
              <Stack direction="row" spacing={3} alignItems="center">
                {exam && <SubjectIcon subject={exam?.subject} />}
                <Stack>
                  <Typography level={"h1"} lineHeight={"normal"}>
                    {exam
                      ? exam?.title + " - " + exam?.description
                      : SkeletonText}
                  </Typography>
                </Stack>
              </Stack>
              {exam ? (
                <Markdown content={exam?.detailDescription} />
              ) : (
                <>
                  <Typography level={"body-xs"}>
                    <Skeleton>
                      Lorem ipsum dolor sit amet, consectetuer adipiscing elit.
                      Aenean commodo ligula eget dolor. Aenean massa. Cum sociis
                      natoque penatibus et magnis dis parturient montes,
                      nascetur ridiculus mus. Donec quam felis, ultricies nec,
                      pellentesque eu, pretium quis, sem. Nulla consequat massa
                      quis enim. Donec pede justo, fringilla vel, aliquet nec,
                      vulputate eget, arcu. In enim justo, rhoncus ut, imperdiet
                      a, venenatis vitae, justo. Nullam dictum felis eu pede
                      mollis pretium. Integer tincidunt. Cras dapibus. Vivamus
                      elementum semper nisi. Aenean vulputate eleifend tellus.
                      Aenean leo ligula, porttitor eu, consequat vitae, eleifend
                      ac, enim. Aliquam lorem ante, dapibus in, viverra quis,
                      feugiat a, tellus. Phasellus{" "}
                    </Skeleton>
                  </Typography>
                </>
              )}
              {!loading && bundleInfo && (
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Stack spacing={2}>
                      <Typography level="title-md" color="primary">
                        Exam Bundle Information
                      </Typography>

                      <Alert
                        color="primary"
                        variant="soft"
                        startDecorator={<InfoOutlinedIcon />}
                      >
                        This exam is part of a bundle. Enrolling will use{" "}
                        {bundleInfo.quotasRequired} {bundleInfo.level} quota.
                      </Alert>

                      <Typography level="body-md" fontWeight="bold">
                        When you enroll, you'll also be enrolled in these exams:
                      </Typography>

                      <List>
                        {bundleInfo.processedExams ? (
                          // Use processedExams if available
                          bundleInfo.processedExams
                            .filter(
                              (bundleExam) => bundleExam.examId !== exam?.examId
                            ) // Filter out current exam
                            .map((bundleExam) => (
                              <ListItem key={bundleExam.examId}>
                                <ListItemDecorator>
                                  <SubjectIcon
                                    subject={bundleExam.subject || "general"}
                                  />
                                </ListItemDecorator>
                                <Typography level="body-sm" ml={2}>
                                  {bundleExam.title} - {bundleExam.description}
                                </Typography>
                              </ListItem>
                            ))
                        ) : (
                          // If processedExams is not available yet, show a loading message
                          <ListItem>
                            <Typography level="body-sm">
                              Loading bundle exams...
                            </Typography>
                          </ListItem>
                        )}
                      </List>
                    </Stack>
                  </CardContent>
                </Card>
              )}
              {!loading &&
                (isHL || isSL ? (
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Stack spacing={1}>
                        <Typography level="title-sm">
                          {bundleInfo
                            ? `${bundleInfo.level} Bundle`
                            : isHL
                            ? "Higher Level (HL)"
                            : "Standard Level (SL)"}{" "}
                          Exam Quota
                          {bundleInfo && (
                            <Typography
                              level="body-xs"
                              sx={{ fontStyle: "italic" }}
                            >
                              (Requires {quotasRequired} quota)
                            </Typography>
                          )}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={2}
                          justifyContent="space-between"
                        >
                          {/* Use bundle level if available, otherwise use exam level */}
                          {bundleInfo ? (
                            <>
                              <Typography level="body-sm">
                                Purchased:{" "}
                                {bundleInfo.level
                                  .toLowerCase()
                                  .includes("higher") ||
                                bundleInfo.level.toLowerCase().includes("hl")
                                  ? userInfo?.purchasedHLQuantity || 0
                                  : userInfo?.purchasedSLQuantity || 0}
                              </Typography>
                              <Typography level="body-sm">
                                Enrolled:{" "}
                                {bundleInfo.level
                                  .toLowerCase()
                                  .includes("higher") ||
                                bundleInfo.level.toLowerCase().includes("hl")
                                  ? userInfo?.enrolledHLQuantity || 0
                                  : userInfo?.enrolledSLQuantity || 0}
                              </Typography>
                              <Typography
                                level="body-sm"
                                sx={{
                                  fontWeight: "bold",
                                  color: hasQuota
                                    ? "success.500"
                                    : "warning.600",
                                }}
                              >
                                Available:{" "}
                                {bundleInfo.level
                                  .toLowerCase()
                                  .includes("higher") ||
                                bundleInfo.level.toLowerCase().includes("hl")
                                  ? hlQuota
                                  : slQuota}
                              </Typography>
                            </>
                          ) : (
                            <>
                              <Typography level="body-sm">
                                Purchased:{" "}
                                {isHL
                                  ? userInfo?.purchasedHLQuantity || 0
                                  : userInfo?.purchasedSLQuantity || 0}
                              </Typography>
                              <Typography level="body-sm">
                                Enrolled:{" "}
                                {isHL
                                  ? userInfo?.enrolledHLQuantity || 0
                                  : userInfo?.enrolledSLQuantity || 0}
                              </Typography>
                              <Typography
                                level="body-sm"
                                sx={{
                                  fontWeight: "bold",
                                  color: hasQuota
                                    ? "success.500"
                                    : "warning.600",
                                }}
                              >
                                Available: {isHL ? hlQuota : slQuota}
                              </Typography>
                            </>
                          )}
                        </Stack>

                        {!hasQuota && (
                          <Alert
                            startDecorator={<InfoOutlinedIcon />}
                            color="warning"
                            variant="soft"
                          >
                            <Stack spacing={1}>
                              <Typography level="body-sm">
                                You don't have enough{" "}
                                {bundleInfo
                                  ? bundleInfo.level
                                      .toLowerCase()
                                      .includes("higher") ||
                                    bundleInfo.level
                                      .toLowerCase()
                                      .includes("hl")
                                    ? "Higher Level (HL)"
                                    : "Standard Level (SL)"
                                  : isHL
                                  ? "Higher Level (HL)"
                                  : "Standard Level (SL)"}{" "}
                                exam quota
                                {bundleInfo
                                  ? ` for this bundle (requires ${quotasRequired} quota)`
                                  : ""}
                                .
                              </Typography>
                            </Stack>
                          </Alert>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                ) : null)}
              <Stack>
                <Typography level={"body-md"} sx={{ fontWeight: 700 }}>
                  {"Date:"}
                </Typography>
                <Typography level={"body-md"}>
                  {date ? date?.format(DATE_FORMAT) : SkeletonText}
                </Typography>
              </Stack>
              <Stack>
                <Typography level={"body-md"} sx={{ fontWeight: 700 }}>
                  {"Time:"}
                </Typography>
                <Typography level={"body-md"}>
                  {date ? date?.format(TIME_FORMAT) : SkeletonText}
                </Typography>
              </Stack>
              <Stack>
                <Typography level={"body-md"} sx={{ fontWeight: 700 }}>
                  {"Duration:"}
                </Typography>
                <Typography level={"body-md"}>
                  {exam ? exam?.examDuration : SkeletonText}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
          <Divider />
          {loading ? (
            <CircularProgress size="sm" />
          ) : (
            <Button
              sx={{ width: "fit-content", minWidth: "200px", alignSelf: "end" }}
              onClick={handleBuy}
              disabled={!hasQuota}
              color={hasQuota ? "primary" : "neutral"}
            >
              {bundleInfo ? "Enroll in Bundle" : "Enroll"}
            </Button>
          )}
        </Stack>
      </PageBodyContainer>
    </PageBase>
  );
};

export default CheckoutPage;
